import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import { useLangFont } from "../../../hooks/useLangFont";

type Lang = "JP" | "中文" | "EN";

const langCodeMap: Record<Lang, string> = {
  "JP": "jp",
  "中文": "zh",
  "EN": "en",
};

const reverseLangMap: Record<string, Lang> = {
  "jp": "JP",
  "zh": "中文",
  "en": "EN",
};

const navHrefs = [
  { key: "nav_gallery", href: "#gallery" },
  { key: "nav_pricing", href: "#pricing" },
  { key: "nav_b2b", href: "#b2b" },
  { key: "nav_contact", href: "#contact" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const { body } = useLangFont();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const activeLang: Lang = reverseLangMap[i18n.language] ?? "JP";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Sync html[lang] attribute with current language for CSS targeting
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleLangChange = (l: Lang) => {
    const code = langCodeMap[l];
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
  };

  const langs: Lang[] = ["JP", "中文", "EN"];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1A2332]/95 backdrop-blur-md shadow-lg py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/09909eceab93e731ba4fd92f69d7cdad/bd37e4785bae3db4f1c4b94805dc789e.jpeg"
            alt="蔵新ロゴ"
            className="h-10 w-10 rounded-full object-cover flex-shrink-0"
          />
          <div className="flex flex-col leading-tight">
            <span
              className="text-[#C9A84C] font-bold text-xl tracking-widest leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              蔵新
            </span>
            <span
              className="text-white/65 leading-tight"
              style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.62rem", letterSpacing: "0.12em" }}
            >
              高級バッグ修復専門
            </span>
            <span
              className="text-white/35 uppercase leading-tight"
              style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.55rem", letterSpacing: "0.1em" }}
            >
              Luxury Bag Restoration Studio
            </span>
          </div>
        </a>

        {/* Desktop Nav */}
        <ul className="hidden lg:flex items-center gap-7">
          {navHrefs.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                onClick={(e) => handleNav(e, item.href)}
                className="text-white/80 hover:text-[#C9A84C] text-sm tracking-widest transition-colors duration-300 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: body }}
              >
                {t(item.key)}
              </a>
            </li>
          ))}
        </ul>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-4">
          <span
            className="text-white/45 text-[10px] tracking-wide whitespace-nowrap"
            style={{ fontFamily: body }}
          >
            {t("nav_hours")}
          </span>

          {/* Language switcher */}
          <div className="flex items-center gap-0.5">
            {langs.map((l, i) => (
              <span key={l} className="flex items-center">
                {i > 0 && <span className="text-white/20 text-[10px] mx-0.5">/</span>}
                <button
                  onClick={() => handleLangChange(l)}
                  className="text-[10px] px-1.5 py-0.5 cursor-pointer transition-colors duration-200 whitespace-nowrap rounded"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: activeLang === l ? 700 : 400,
                    color: activeLang === l ? "#C9A84C" : "rgba(255,255,255,0.45)",
                    background: "none",
                    border: "none",
                  }}
                >
                  {l}
                </button>
              </span>
            ))}
          </div>

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => handleNav(e, "#contact")}
            className="btn-lift hidden lg:flex items-center gap-2 bg-[#C9A84C] hover:bg-[#B8973B] text-[#1A2332] text-xs font-bold px-4 py-2.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {t("nav_cta")}
            <i className="ri-arrow-right-line text-xs" />
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-white text-2xl cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </div>

      {/* Mobile Dropdown */}
      {menuOpen && (
        <div className="lg:hidden bg-[#1A2332]/98 px-6 pb-6 pt-2 flex flex-col gap-4">
          {navHrefs.map((item) => (
            <a
              key={item.key}
              href={item.href}
              onClick={(e) => handleNav(e, item.href)}
              className="text-white/80 hover:text-[#C9A84C] text-sm tracking-widest transition-colors duration-300 cursor-pointer"
            >
              {t(item.key)}
            </a>
          ))}
          <div className="flex items-center gap-2 pt-1">
            {langs.map((l, i) => (
              <span key={l} className="flex items-center">
                {i > 0 && <span className="text-white/20 text-xs mx-1">/</span>}
                <button
                  onClick={() => handleLangChange(l)}
                  className="text-xs cursor-pointer"
                  style={{
                    fontFamily: "'Montserrat', sans-serif",
                    color: activeLang === l ? "#C9A84C" : "rgba(255,255,255,0.45)",
                    background: "none",
                    border: "none",
                  }}
                >
                  {l}
                </button>
              </span>
            ))}
          </div>
          <a
            href="#contact"
            onClick={(e) => handleNav(e, "#contact")}
            className="bg-[#C9A84C] text-[#1A2332] text-sm font-bold px-5 py-2.5 rounded-full text-center whitespace-nowrap cursor-pointer"
          >
            {t("nav_cta")}
          </a>
          <button
            onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); setMenuOpen(false); }}
            className="flex items-center justify-center gap-2 border border-white/20 text-white/50 hover:text-[#C9A84C] hover:border-[#C9A84C]/40 text-xs py-2.5 rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans JP', sans-serif", background: "none" }}
          >
            <i className="ri-arrow-up-line text-xs" />
            {t("nav_scroll_top")}
          </button>
        </div>
      )}
    </nav>
  );
}
